import React, { useState } from 'react'

interface DatePickerSandboxProps {
  theme: 'light' | 'dark'
}

interface SandboxCardProps {
  title: string
  description: string
  children: React.ReactNode
  isDarkMode: boolean
}

function SandboxCard({ title, description, children, isDarkMode }: SandboxCardProps) {
  return (
    <div className={`rounded-lg p-6 mb-6 border ${
      isDarkMode 
        ? 'bg-gray-800 border-gray-600' 
        : 'bg-white border-gray-200'
    } shadow-lg`}>
      <h3 className={`text-3xl font-semibold mb-2 ${
        isDarkMode ? 'text-white' : 'text-gray-900'
      }`}>
        {title}
      </h3>
      <p className={`text-sm mb-4 ${
        isDarkMode ? 'text-gray-300' : 'text-gray-600'
      }`}>
        {description}
      </p>
      {children}
    </div>
  )
}

function DatePickerSandbox({ theme }: DatePickerSandboxProps) {
  const isDarkMode = theme === 'dark'
  
  // Native HTML5 pickers
  const [nativeDate, setNativeDate] = useState('')
  const [nativeTime, setNativeTime] = useState('')
  const [nativeDateTime, setNativeDateTime] = useState('')
  
  // Custom styled pickers
  const [customDate, setCustomDate] = useState('')
  const [customTime, setCustomTime] = useState('')
  
  // Week picker
  const [weekDate, setWeekDate] = useState('')
  
  // Month picker
  const [monthDate, setMonthDate] = useState('')

  const textColor = isDarkMode ? 'text-white' : 'text-gray-900'
  const subtitleColor = isDarkMode ? 'text-gray-300' : 'text-gray-600'

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className={`text-5xl font-bold mb-3 ${textColor}`}>
          📅 Date & Time Picker Sandbox
        </h1>
        <p className={`text-3xl ${subtitleColor}`}>
          Explore different web-based date/time picker implementations. 
          Perfect for prototyping and comparing mobile vs desktop behavior.
        </p>
      </div>

      {/* Native HTML5 Date Picker */}
      <SandboxCard 
        title="📱 Native HTML5 Date Picker"
        description="Uses the browser's native date picker. Automatically adapts to iOS/Android/desktop styling."
        isDarkMode={isDarkMode}
      >
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              Date Picker
            </label>
            <input
              type="date"
              value={nativeDate}
              onChange={(e) => setNativeDate(e.target.value)}
              className={`w-full p-3 rounded-md border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            {nativeDate && (
              <p className={`text-sm mt-2 ${subtitleColor}`}>
                Selected: {new Date(nativeDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}
          </div>
        </div>
      </SandboxCard>

      {/* Native HTML5 Time Picker */}
      <SandboxCard 
        title="⏰ Native HTML5 Time Picker"
        description="Native time picker with automatic 12/24 hour format based on user's system settings."
        isDarkMode={isDarkMode}
      >
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              Time Picker
            </label>
            <input
              type="time"
              value={nativeTime}
              onChange={(e) => setNativeTime(e.target.value)}
              className={`w-full p-3 rounded-md border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            {nativeTime && (
              <p className={`text-sm mt-2 ${subtitleColor}`}>
                Selected: {new Date(`2000-01-01T${nativeTime}`).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
        </div>
      </SandboxCard>

      {/* Native HTML5 DateTime Picker */}
      <SandboxCard 
        title="📅⏰ Native HTML5 DateTime Picker"
        description="Combined date and time picker in a single input field."
        isDarkMode={isDarkMode}
      >
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              DateTime Picker
            </label>
            <input
              type="datetime-local"
              value={nativeDateTime}
              onChange={(e) => setNativeDateTime(e.target.value)}
              className={`w-full p-3 rounded-md border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            {nativeDateTime && (
              <p className={`text-sm mt-2 ${subtitleColor}`}>
                Selected: {new Date(nativeDateTime).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
        </div>
      </SandboxCard>

      {/* Week Picker */}
      <SandboxCard 
        title="📅 Week Picker"
        description="Select specific weeks - great for scheduling and time tracking applications."
        isDarkMode={isDarkMode}
      >
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              Week Picker
            </label>
            <input
              type="week"
              value={weekDate}
              onChange={(e) => setWeekDate(e.target.value)}
              className={`w-full p-3 rounded-md border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            {weekDate && (
              <p className={`text-sm mt-2 ${subtitleColor}`}>
                Selected: Week {weekDate.split('-W')[1]} of {weekDate.split('-W')[0]}
              </p>
            )}
          </div>
        </div>
      </SandboxCard>

      {/* Month Picker */}
      <SandboxCard 
        title="📅 Month Picker"
        description="Select specific months - perfect for reports and monthly planning."
        isDarkMode={isDarkMode}
      >
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              Month Picker
            </label>
            <input
              type="month"
              value={monthDate}
              onChange={(e) => setMonthDate(e.target.value)}
              className={`w-full p-3 rounded-md border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            {monthDate && (
              <p className={`text-sm mt-2 ${subtitleColor}`}>
                Selected: {new Date(monthDate + '-01').toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long'
                })}
              </p>
            )}
          </div>
        </div>
      </SandboxCard>

      {/* Custom Styled Examples */}
      <SandboxCard 
        title="🎨 Custom Styled Examples"
        description="Same functionality with custom styling to match your brand."
        isDarkMode={isDarkMode}
      >
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              Custom Date Picker
            </label>
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className={`w-full p-4 rounded-lg border-2 ${
                isDarkMode 
                  ? 'bg-gray-800 border-blue-500 text-white' 
                  : 'bg-blue-50 border-blue-400 text-gray-900'
              } focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-200`}
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              Custom Time Picker
            </label>
            <input
              type="time"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              className={`w-full p-4 rounded-lg border-2 ${
                isDarkMode 
                  ? 'bg-gray-800 border-green-500 text-white' 
                  : 'bg-green-50 border-green-400 text-gray-900'
              } focus:ring-4 focus:ring-green-200 focus:border-green-600 transition-all duration-200`}
            />
          </div>
        </div>
      </SandboxCard>

      {/* Platform Comparison */}
      <div className={`rounded-lg p-6 border ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-600' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <h3 className={`text-3xl font-semibold mb-4 ${textColor}`}>
          📱 Platform Behavior
        </h3>
        <div className="space-y-3 text-sm">
          <div className={subtitleColor}>
            <strong className={textColor}>iOS Safari:</strong> Native wheel picker with smooth scrolling
          </div>
          <div className={subtitleColor}>
            <strong className={textColor}>Android Chrome:</strong> Material Design picker with calendar view
          </div>
          <div className={subtitleColor}>
            <strong className={textColor}>Desktop:</strong> Varies by browser - Chrome, Firefox, Safari each have different styles
          </div>
          <div className={subtitleColor}>
            <strong className={textColor}>Accessibility:</strong> All pickers support screen readers and keyboard navigation
          </div>
        </div>
      </div>

      {/* Usage Notes */}
      <div className={`mt-8 p-4 rounded-lg ${
        isDarkMode 
          ? 'bg-blue-900 bg-opacity-20 border border-blue-700' 
          : 'bg-blue-50 border border-blue-200'
      }`}>
        <h4 className={`font-semibold mb-2 ${textColor}`}>
          💡 Usage Notes
        </h4>
        <ul className={`text-sm space-y-1 ${subtitleColor}`}>
          <li>• These pickers work immediately without any additional libraries</li>
          <li>• Perfect for prototyping and testing user experience</li>
          <li>• Can be styled with CSS to match your brand</li>
          <li>• Automatically handle internationalization and localization</li>
          <li>• Great starting point before implementing custom solutions</li>
        </ul>
      </div>
    </div>
  )
}

export default DatePickerSandbox